import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';

import { withStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Tooltip from '@material-ui/core/Tooltip';

import { RecordingItem } from '../RecordingItem';
import { REANALYZE_RECORDING, DELETE_RECORDING } from '../../mutations';
import { interpretReanalyzeOutcome, ReanalyzeFeedback } from '../../utilities/reanalyzeFeedback';
import { ANALYSED_COLUMN_HINT } from '../../utilities/analysedAt';
import { RECORDING_FLAG_OPTIONS } from '../../utilities/recordingFlags';
import { useRefreshWhileAnalysing } from '../../hooks/useRefreshWhileAnalysing';

/**
 * Styling
 */
const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  row: {
    '& > th': {
      fontWeight: 'bold',
    },
  },
  container: {
    marginTop: theme.spacing(12),
  },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    textAlign: 'center',
  },
  body: {
    fontSize: 16,
  },
}))(TableCell);

export const GET_RECORDINGS_BY_ID = gql`
  query GetRecordingsById($recordingIds: [String]) {
    getRecordingsById(recordingIds: $recordingIds) {
      id
      fileName
      recordingStartedAt
      channelCount
      uploadedAt
      metadata {
        createdAt
        updatedAt
      }
      recordingIsUploaded
      isBeingAnalysed
      downloadUrl
      comment
      analysis {
        id
        rpeaksCount
        success
        error
        analysedAt
        # project_management#533: surface kept-last-good failures
        lastFailedAnalysisAt
        lastAnalysisError
      }
      flags
    }
  }
`;

// eslint-disable-next-line no-undef
export function RecordingView(): JSX.Element {
  const classes = useStyles();

  const query = new URLSearchParams(useLocation().search);
  const recordingIds = query.get('id')?.split(',') ?? [];

  /**
   * Queries and mutations
   */
  const { data, loading, error, refetch } = useQuery(GET_RECORDINGS_BY_ID, {
    variables: {
      recordingIds,
    },
  });
  // project_management#507: re-read the rows so `isBeingAnalysed` (and then
  // the new "Analysed" date) show up — this view used to fire and forget.
  const [reanalyze] = useMutation(REANALYZE_RECORDING, {
    refetchQueries: [{ query: GET_RECORDINGS_BY_ID, variables: { recordingIds } }],
  });
  const [deleteRecording] = useMutation(DELETE_RECORDING);
  const [reanalyzeFeedback, setReanalyzeFeedback] = useState<ReanalyzeFeedback | null>(null);

  useRefreshWhileAnalysing(
    Boolean(
      data?.getRecordingsById?.some(
        (recording: { isBeingAnalysed?: boolean | null }) => recording?.isBeingAnalysed,
      ),
    ),
    () => refetch().catch(() => undefined),
  );

  /**
   * project_management#508 — see RecordingList for the reasoning; this view
   * renders the same rows and had the same silent mutation.
   */
  const handleReanalyze = async ({
    recordingId,
    fileName,
  }: {
    recordingId: string;
    fileName?: string | null;
  }) => {
    try {
      const result = await reanalyze({
        variables: { analyzeRecordingRecordingId: recordingId },
      });

      setReanalyzeFeedback(interpretReanalyzeOutcome({ data: result?.data, fileName }));
    } catch (mutationError) {
      setReanalyzeFeedback(
        interpretReanalyzeOutcome({ error: mutationError as { message?: string }, fileName }),
      );
    }
  };

  /**
   * Throw error if recording Id was not passed as argument
   */
  if (!recordingIds.length) return <div>Recording chart loading failed. Missing recording ID</div>;

  /**
   * Load and handle recording data
   */
  if (loading || !data) return <div>Loading recording data...</div>;
  if (error) return <div>Error occured while loading recording data: {error}</div>;

  // project_management#580: generated schema enum, not runtime introspection.
  const flagOptions: string[] = RECORDING_FLAG_OPTIONS;

  return (
    <Container className={classes.container} maxWidth={false}>
      <TableContainer className={classes.container} component={Paper}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCell />
              <StyledTableCell align="right">Started</StyledTableCell>
              <StyledTableCell align="right">Updated</StyledTableCell>
              <StyledTableCell align="right">
                <Tooltip title={ANALYSED_COLUMN_HINT}>
                  <span>Analysed</span>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="right">Channel</StyledTableCell>
              <StyledTableCell align="right">File Name</StyledTableCell>
              <StyledTableCell align="right">Analysis Status</StyledTableCell>
              <StyledTableCell align="right">rPeaks</StyledTableCell>
              <StyledTableCell align="right">Comments</StyledTableCell>
              <StyledTableCell align="right">Flags</StyledTableCell>
              <StyledTableCell align="right">Download Annotations</StyledTableCell>
              <StyledTableCell align="right">Download File</StyledTableCell>
              <StyledTableCell align="right">Reanalyze</StyledTableCell>
              <StyledTableCell align="right">Delete</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.getRecordingsById.map(
              ({
                fileName,
                id,
                recordingStartedAt,
                metadata,
                analysis,
                channelCount,
                comment,
                flags,
                isBeingAnalysed,
              }) => (
                <RecordingItem
                  key={id}
                  recordingId={id}
                  analysis={analysis}
                  channelCount={channelCount}
                  comment={comment}
                  fileName={fileName}
                  flagOptions={flagOptions}
                  flags={flags ?? []}
                  updatedAt={metadata?.updatedAt}
                  isBeingAnalysed={isBeingAnalysed}
                  recordingStartedAt={recordingStartedAt}
                  onDelete={deleteRecording}
                  onReanalyze={handleReanalyze}
                />
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* project_management#508 */}
      <Snackbar
        open={Boolean(reanalyzeFeedback)}
        autoHideDuration={reanalyzeFeedback?.severity === 'error' ? null : 6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setReanalyzeFeedback(null)}
      >
        <Alert
          severity={reanalyzeFeedback?.severity ?? 'success'}
          onClose={() => setReanalyzeFeedback(null)}
        >
          {reanalyzeFeedback?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
